import fuzzysort from "fuzzysort";
import type { Room } from "sdk";
import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import { useApi } from "@/api";
import { useCtx } from "@/app/context";
import { Icon } from "@/atoms/Icon";
import { UnicodeEmoji } from "@/atoms/UnicodeEmoji";
import { RoomIcon } from "@/components/shared/User";
import { useMenu } from "@/contexts/mod";
import {
	type EmojiData,
	emojiResource,
	getEmojiHex,
	rawEmojiResource,
} from "@/lib/emoji";
import { getThumbFromId } from "@/media/util";
import {
	icEmojiActivities,
	icEmojiFaces,
	icEmojiFlags,
	icEmojiFood,
	icEmojiNature,
	icEmojiObjects,
	icEmojiPeople,
	icEmojiPlaces,
	icEmojiSymbols,
} from "@/utils/icons";
import { Search } from "./Search";

type UnifiedEmoji = {
	type: "standard" | "custom";
	label: string;
	unicode?: string;
	hexcode?: string;
	order?: number;
	id?: string;
	media_id?: string;
	animated?: boolean;
	room_id?: string;
};

type EmojiGroup = {
	id: string | number;
	name: string;
	icon?: string;
	room?: Room;
	emojis: UnifiedEmoji[];
};
const parseEmoji = (): EmojiGroup[] => {
	const emojis = emojiResource();
	if (!emojis.size) return [];

	const groups: EmojiData[][] = [[], [], [], [], [], [], [], [], [], []];
	for (const emoji of emojis.values()) {
		// group 2 only has modifiers (eg. for skin tone)
		if (emoji.group === 2) continue;

		groups[emoji.group].push(emoji);
	}
	return groups
		.map((groupEmojis, i) => ({
			id: i,
			name: getGroupName(i) || "Unknown",
			icon: getGroupIcon(i),
			emojis: groupEmojis
				.map((e) => ({
					type: "standard" as const,
					label: e.label,
					unicode: e.char,
					hexcode: e.hexcode,
					order: e.order,
				}))
				.sort((a, b) => a.order - b.order),
		}))
		.filter((g) => g.name !== "Unknown");
};

const getGroupIcon = (id: number) => {
	switch (id) {
		case 0:
			return icEmojiFaces;
		case 1:
			return icEmojiPeople;
		case 3:
			return icEmojiNature;
		case 4:
			return icEmojiFood;
		case 5:
			return icEmojiPlaces;
		case 6:
			return icEmojiActivities;
		case 7:
			return icEmojiObjects;
		case 8:
			return icEmojiSymbols;
		case 9:
			return icEmojiFlags;
	}
};

// TODO: use emojiLabels().groups
const getGroupName = (id: number) => {
	switch (id) {
		case 0:
			return "Faces";
		case 1:
			return "People";
		case 2:
			// this category is skin tones and hair modifiers, so we ignore it
			return;
		case 3:
			return "Animals & nature";
		case 4:
			return "Food & Drink";
		case 5:
			return "Travel & Places";
		case 6:
			return "Activities";
		case 7:
			return "Objects";
		case 8:
			return "Symbols";
		case 9:
			return "Flags";
	}
};

type EmojiPickerProps = {
	selected: (value: string | null, shiftKey: boolean) => void;
};

export const EmojiPicker = (props: EmojiPickerProps) => {
	const api = useApi();
	const ctx = useCtx();
	const { setMenu } = useMenu();
	const [search, setSearch] = createSignal("");
	const [hover, setHover] = createSignal<UnifiedEmoji>();

	const standardGroups = createMemo(() => parseEmoji());

	const [customGroupsResource] = createResource(
		() => [...api.rooms.cache.keys()],
		async (roomIds) => {
			await api.emoji.listAllCustom(roomIds);
			return roomIds
				.map((id) => api.rooms.cache.get(id))
				.filter((r): r is Room => r !== undefined)
				.map((room) => {
					const emojis = [...api.emoji.cache.values()].filter((e) => {
						if (e.owner?.owner === "Room") {
							return e.owner.room_id === room.id;
						}
						return false;
					});

					if (emojis.length === 0) return null;

					return {
						id: `room-${room.id}`,
						name: room.name,
						room,
						emojis: emojis.map((e) => ({
							type: "custom",
							label: e.name,
							id: e.id,
							media_id: e.media_id,
							animated: e.animated,
							room_id: room.id,
						})),
					} as EmojiGroup;
				})
				.filter((r) => r !== null) as EmojiGroup[];
		},
	);

	const favoritesGroup = createMemo<EmojiGroup | null>(() => {
		const keys = ctx.preferences().frontend.favorite_emoji ?? [];
		if (keys.length === 0) return null;

		const emojis: UnifiedEmoji[] = [];
		for (const key of keys) {
			if (key.startsWith("c:")) {
				const emoji = api.emoji.cache.get(key.slice(2));
				if (!emoji) continue;
				const owner = emoji.owner;
				emojis.push({
					type: "custom",
					label: emoji.name,
					id: emoji.id,
					media_id: emoji.media_id,
					animated: emoji.animated,
					room_id: owner?.owner === "Room" ? owner.room_id : undefined,
				});
			} else if (key.startsWith("t:")) {
				const unicode = key.slice(2);
				const hexcode = getEmojiHex(unicode);
				const data = emojiResource().get(hexcode.toUpperCase());
				emojis.push({
					type: "standard",
					label: data?.label ?? unicode,
					unicode,
					hexcode,
				});
			}
		}

		if (emojis.length === 0) return null;
		return { id: "favorites", name: "Favorites", emojis };
	});

	const allGroups = createMemo(() => {
		const standard = standardGroups();
		const custom = customGroupsResource() || [];
		const favorites = favoritesGroup();
		return favorites
			? [favorites, ...custom, ...standard]
			: [...custom, ...standard];
	});

	// TODO: convert this to createMemo
	const [shortcode] = createResource(hover, async (h) => {
		if (!h) return "";
		if (h.type === "custom") return h.label;
		if (!h.unicode) return "";
		// Get first shortcode from emojiResource data
		const emojis = emojiResource();
		if (!emojis) return "";
		const emoji = emojis.get(h.hexcode?.toUpperCase()!);
		return emoji?.shortcodes?.[0] ?? h.label;
	});

	const filtered = () => {
		const groups = allGroups();
		const s = search();
		if (!s) return groups;

		return groups
			.map((group) => {
				const results = fuzzysort.go(s, group.emojis, {
					key: "label",
					threshold: -1000,
				});
				return {
					...group,
					emojis: results
						.map((j) => j.obj)
						.sort((a, b) => {
							if (a.type === "standard" && b.type === "standard") {
								return (
									parseInt(a.hexcode!, 16) - parseInt(b.hexcode!, 16) ||
									(a.order! > b.order! ? 1 : -1)
								);
							}
							return a.label.localeCompare(b.label);
						}),
				};
			})
			.filter((g) => g.emojis.length > 0);
	};

	const handleSubmit = async (value: string, e: KeyboardEvent) => {
		if (e.ctrlKey) {
			props.selected(value, e.shiftKey);
		} else {
			const f = filtered();
			if (!f) return;
			const group = f.find((i) => i.emojis.length);
			const emoji = group?.emojis[0];
			if (emoji) {
				if (emoji.type === "standard") {
					props.selected(emoji.unicode!, e.shiftKey);
				} else {
					props.selected(`<:${emoji.label}:${emoji.id}>`, e.shiftKey);
				}
			}
		}
	};

	let emojisContainerRef!: HTMLDivElement;

	const scrollToCategory = (id: string | number) => {
		const el = document.getElementById(`emoji-cat-${id}`);
		if (el) {
			el.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	return (
		<div class="emoji-picker" onClick={(e) => e.stopPropagation()}>
			<header>
				<Search
					placeholder="shift for multiple, ctrl for raw text"
					value={search}
					onInput={setSearch}
					onSubmit={handleSubmit}
					onEscape={() => props.selected(null, false)}
					ref={(el) => queueMicrotask(() => el.focus())}
				/>
				{/* TODO: (low priority) skin tone */}
				<div
					style="font-size: 24px; height: 28px; width: 28px; margin-left: 8px; cursor: pointer"
					hidden
				></div>
			</header>
			<div class="categories">
				<For each={allGroups()}>
					{(cat) => (
						<button
							type="button"
							class="button"
							onClick={() => scrollToCategory(cat.id)}
						>
							<Show
								when={cat.room}
								fallback={
									<Show
										when={cat.icon}
										fallback={<span class="favorite-cat">★</span>}
									>
										<Icon src={cat.icon!} />
									</Show>
								}
							>
								<RoomIcon room={cat.room!} />
							</Show>
						</button>
					)}
				</For>
			</div>
			<div class="emojis" ref={emojisContainerRef!}>
				<For each={filtered()}>
					{(group) => (
						<div id={`emoji-cat-${group.id}`}>
							<div class="label">{group.name}</div>
							<div class="group">
								<For each={group.emojis}>
									{(emoji) => (
										<div
											class="emojiwrap"
											onMouseOver={() => setHover(emoji)}
											onFocus={() => setHover(emoji)}
											onClick={(e) => {
												if (emoji.type === "standard") {
													props.selected(emoji.unicode!, e.shiftKey);
												} else {
													props.selected(
														`<:${emoji.label}:${emoji.id}>`,
														e.shiftKey,
													);
												}
											}}
											onContextMenu={(e) => {
												e.preventDefault();
												e.stopPropagation();
												if (emoji.type === "standard") {
													setMenu({
														x: e.clientX,
														y: e.clientY,
														type: "emoji",
														key: `t:${emoji.unicode}`,
														content: emoji.unicode,
													});
												} else {
													setMenu({
														x: e.clientX,
														y: e.clientY,
														type: "emoji",
														key: `c:${emoji.id}`,
														id: emoji.id,
														name: emoji.label,
														animated: emoji.animated,
													});
												}
											}}
											data-emoji-hex={emoji.hexcode}
										>
											{emoji.type === "standard" ? (
												<UnicodeEmoji hex={emoji.hexcode!.toUpperCase()} />
											) : (
												<img
													src={getThumbFromId(emoji.media_id!, 64)}
													class="emoji custom-emoji"
												/>
											)}
										</div>
									)}
								</For>
							</div>
						</div>
					)}
				</For>
				<Show
					when={
						!rawEmojiResource.loading &&
						filtered().every((i) => i.emojis.length === 0)
					}
				>
					<div style="display: flex; align-items: center; justify-content: center; width 100%; height: 100%">
						no emoji :(
					</div>
				</Show>
			</div>
			<div class="preview">
				<Show when={hover()}>
					{(h) => (
						<>
							{h().type === "standard" ? (
								<UnicodeEmoji hex={getEmojiHex(h().unicode!)} />
							) : (
								<img
									src={getThumbFromId(h().media_id!, 64)}
									class="emoji custom-emoji"
								/>
							)}
							<b>:{shortcode()}:</b>
							{/* <span style="color: var(--fg-dim)">{h().tags?.join(", ")}</span> */}
						</>
					)}
				</Show>
			</div>
		</div>
	);
};
